import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { CartProvider } from './context/CartContext';
import Header from './components/Header';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: #f8f9fa;
    color: #1a1a1a;
    line-height: 1.6;
  }

  html {
    scroll-behavior: smooth;
  }

  a {
    color: inherit;
    text-decoration: none;
  }

  button {
    font-family: inherit;
  }

  input, textarea, select {
    font-family: inherit;
  }

  img {
    max-width: 100%;
    height: auto;
  }

  /* Custom scrollbar */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  ::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Main = styled.main`
  flex: 1;
`;

const Footer = styled.footer`
  background: #1a1a1a;
  color: white;
  padding: 3rem 2rem 2rem;
  margin-top: 4rem;
`;

const FooterContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
`;

const FooterSection = styled.div``;

const FooterTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 1rem;
`;

const FooterList = styled.ul`
  list-style: none;
`;

const FooterListItem = styled.li`
  margin-bottom: 0.5rem;
`;

const FooterLink = styled.a`
  color: #ccc;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: white;
  }
`;

const FooterBottom = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #333;
  text-align: center;
  color: #ccc;
`;

const NotFoundPage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  text-align: center;
  padding: 2rem;
`;

const NotFoundTitle = styled.h1`
  font-size: 4rem;
  font-weight: bold;
  color: #1a1a1a;
  margin-bottom: 1rem;
`;

const NotFoundText = styled.p`
  font-size: 1.2rem;
  color: #666;
  margin-bottom: 2rem;
`;

const NotFoundButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #1a1a1a;
  color: white;
  padding: 1rem 2rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: #333;
    transform: translateY(-1px);
  }
`;

const App: React.FC = () => {
  return (
    <CartProvider>
      <Router>
        <GlobalStyle />
        <AppContainer>
          <Header />
          <Main>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/product/:id" element={<ProductDetailPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/category/:category" element={<ProductsPage />} />
              <Route path="/search" element={<ProductsPage />} />
              <Route path="*" element={
                <NotFoundPage>
                  <NotFoundTitle>404</NotFoundTitle>
                  <NotFoundText>Aradığınız sayfa bulunamadı.</NotFoundText>
                  <NotFoundButton href="/">Ana Sayfaya Dön</NotFoundButton>
                </NotFoundPage>
              } />
            </Routes>
          </Main>
          
          <Footer>
            <FooterContainer>
              <FooterSection>
                <FooterTitle>Fashion Connect</FooterTitle>
                <p style={{ color: '#ccc', lineHeight: 1.6 }}>
                  Modern erkek giyim koleksiyonları ile stilinizi yansıtın. 
                  Kaliteli ürünler, uygun fiyatlar ve hızlı teslimat.
                </p>
              </FooterSection>

              <FooterSection>
                <FooterTitle>Kategoriler</FooterTitle>
                <FooterList>
                  <FooterListItem>
                    <FooterLink href="/category/takim-elbise">Takım Elbise</FooterLink>
                  </FooterListItem>
                  <FooterListItem>
                    <FooterLink href="/category/gomlek">Gömlek</FooterLink>
                  </FooterListItem>
                  <FooterListItem>
                    <FooterLink href="/category/pantolon">Pantolon</FooterLink>
                  </FooterListItem>
                  <FooterListItem>
                    <FooterLink href="/category/ayakkabi">Ayakkabı</FooterLink>
                  </FooterListItem>
                  <FooterListItem>
                    <FooterLink href="/category/aksesuar">Aksesuar</FooterLink>
                  </FooterListItem>
                </FooterList>
              </FooterSection>

              <FooterSection>
                <FooterTitle>Müşteri Hizmetleri</FooterTitle>
                <FooterList>
                  <FooterListItem>
                    <FooterLink href="/iletisim">İletişim</FooterLink>
                  </FooterListItem>
                  <FooterListItem>
                    <FooterLink href="/iade-degisim">İade & Değişim</FooterLink>
                  </FooterListItem>
                  <FooterListItem>
                    <FooterLink href="/kargo">Kargo Bilgileri</FooterLink>
                  </FooterListItem>
                  <FooterListItem>
                    <FooterLink href="/sss">Sıkça Sorulan Sorular</FooterLink>
                  </FooterListItem>
                  <FooterListItem>
                    <FooterLink href="/beden-rehberi">Beden Rehberi</FooterLink>
                  </FooterListItem>
                </FooterList>
              </FooterSection>

              <FooterSection>
                <FooterTitle>Kurumsal</FooterTitle>
                <FooterList>
                  <FooterListItem>
                    <FooterLink href="/hakkimizda">Hakkımızda</FooterLink>
                  </FooterListItem>
                  <FooterListItem>
                    <FooterLink href="/magazalar">Mağazalarımız</FooterLink>
                  </FooterListItem>
                  <FooterListItem>
                    <FooterLink href="/kariyer">Kariyer</FooterLink>
                  </FooterListItem>
                  <FooterListItem>
                    <FooterLink href="/gizlilik">Gizlilik Politikası</FooterLink>
                  </FooterListItem>
                  <FooterListItem>
                    <FooterLink href="/kullanim-kosullari">Kullanım Koşulları</FooterLink>
                  </FooterListItem>
                </FooterList>
              </FooterSection>
            </FooterContainer>

            <FooterBottom>
              <p>&copy; 2025 Fashion Connect. Tüm hakları saklıdır.</p>
            </FooterBottom>
          </Footer>
        </AppContainer>
      </Router>
    </CartProvider>
  );
};

export default App;